import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, DollarSign, X, AlertCircle, Search, Calendar, User, Package, Building2 } from 'lucide-react';
import { getApiUrl, getAuthHeaders } from '../utils/auth';
import Pagination, { usePagination } from './Pagination';
import SearchableUserDropdown from './SearchableUserDropdown';
import StyledDropdown from './StyledDropdown';

const SalesManagement = () => {
  const [sales, setSales] = useState([]);
  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingSale, setEditingSale] = useState(null);
  const [viewingSale, setViewingSale] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  // Filter state
  const [filterMonth, setFilterMonth] = useState(new Date().getMonth() + 1);
  const [filterYear, setFilterYear] = useState(new Date().getFullYear());
  const [filterGroup, setFilterGroup] = useState('');
  const [filterUser, setFilterUser] = useState('');
  const [filterProduct, setFilterProduct] = useState('');

  const [formData, setFormData] = useState({
    userId: '',
    productId: '',
    clientName: '',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    description: '',
  });

  // Fetch data
  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');

      const params = new URLSearchParams();
      if (filterMonth) params.append('month', filterMonth);
      if (filterYear) params.append('year', filterYear);
      if (filterUser) params.append('userId', filterUser);
      if (filterProduct) params.append('productId', filterProduct);
      if (filterGroup) params.append('groupId', filterGroup);

      const [salesRes, usersRes, productsRes, groupsRes] = await Promise.all([
        fetch(`${getApiUrl()}/api/sales?${params.toString()}`, { headers: getAuthHeaders() }),
        fetch(`${getApiUrl()}/api/sales/users/list`, { headers: getAuthHeaders() }),
        fetch(`${getApiUrl()}/api/sales/products/list`, { headers: getAuthHeaders() }),
        fetch(`${getApiUrl()}/api/groups`, { headers: getAuthHeaders() }),
      ]);

      const salesResult = await salesRes.json();
      const usersResult = await usersRes.json();
      const productsResult = await productsRes.json();
      const groupsResult = await groupsRes.json();

      if (salesResult.success) {
        setSales(salesResult.data);
      }

      if (usersResult.success) {
        setUsers(usersResult.data);
      }

      if (productsResult.success) {
        setProducts(productsResult.data);
      }

      if (groupsResult.success) {
        setGroups(groupsResult.data);
      }
    } catch (err) {
      console.error('Fetch data error:', err);
      setError('Unable to connect to server');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filterMonth, filterYear, filterGroup, filterUser, filterProduct]);

  const resetForm = () => {
    setFormData({
      userId: '',
      productId: '',
      clientName: '',
      amount: '',
      date: new Date().toISOString().split('T')[0],
      description: '',
    });
  };

  const handleCreateSale = async (e) => {
    e.preventDefault();
    try {
      setError('');
      setSuccess('');

      const response = await fetch(`${getApiUrl()}/api/sales`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (result.success) {
        setSuccess('Sale recorded successfully');
        setShowCreateModal(false);
        resetForm();
        fetchData();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(result.message || 'Failed to record sale');
      }
    } catch (err) {
      console.error('Create sale error:', err);
      setError('Failed to record sale');
    }
  };

  const handleUpdateSale = async (e) => {
    e.preventDefault();
    try {
      setError('');
      setSuccess('');

      const response = await fetch(`${getApiUrl()}/api/sales/${editingSale.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (result.success) {
        setSuccess('Sale updated successfully');
        setShowEditModal(false);
        setEditingSale(null);
        resetForm();
        fetchData();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(result.message || 'Failed to update sale');
      }
    } catch (err) {
      console.error('Update sale error:', err);
      setError('Failed to update sale');
    }
  };

  const handleDeleteSale = async (id) => {
    try {
      setError('');
      setSuccess('');

      const response = await fetch(`${getApiUrl()}/api/sales/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });

      const result = await response.json();

      if (result.success) {
        setSuccess('Sale deleted successfully');
        setDeleteConfirm(null);
        fetchData();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        setError(result.message || 'Failed to delete sale');
      }
    } catch (err) {
      console.error('Delete sale error:', err);
      setError('Failed to delete sale');
    }
  };

  const openEditModal = (sale) => {
    setEditingSale(sale);
    setFormData({
      userId: sale.userId,
      productId: sale.productId.toString(),
      clientName: sale.clientName || '',
      amount: parseFloat(sale.amount).toString(),
      date: new Date(sale.date).toISOString().split('T')[0],
      description: sale.description || '',
    });
    setShowEditModal(true);
  };

  const openViewModal = (sale) => {
    setViewingSale(sale);
    setShowViewModal(true);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-MY', {
      style: 'currency',
      currency: 'MYR',
    }).format(amount);
  };

  const formatNumber = (value) => {
    if (!value) return '';
    const num = parseFloat(value.toString().replace(/,/g, ''));
    if (isNaN(num)) return '';
    return new Intl.NumberFormat('en-MY').format(num);
  };

  const parseFormattedNumber = (value) => {
    if (!value) return '';
    return value.toString().replace(/,/g, '');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-MY', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getSelectedProduct = () => {
    return products.find(p => p.id === parseInt(formData.productId));
  };

  // Generate month options
  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ];

  // Generate year options dynamically
  // From 2020 (or system start year) to current year + 1 (for future planning)
  const currentYear = new Date().getFullYear();
  const startYear = 2020; // System start year
  const endYear = currentYear + 1; // Allow next year for planning
  const years = Array.from({ length: endYear - startYear + 1 }, (_, i) => endYear - i);

  // Filter users by selected group
  const filteredUsers = filterGroup
    ? users.filter(user => user.group?.id === parseInt(filterGroup))
    : users;

  // Pagination
  const {
    currentPage,
    paginatedItems: paginatedSales,
    totalItems,
    handlePageChange,
    resetPage,
  } = usePagination(sales, 10);

  // Reset page when filters change
  useEffect(() => {
    resetPage();
  }, [filterMonth, filterYear, filterGroup, filterUser, filterProduct]);

  if (loading && sales.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-dark-text">Sales Management</h1>
          <p className="text-gray-600 dark:text-dark-muted mt-1">Record and manage sales transactions</p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowCreateModal(true);
          }}
          className="flex items-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-lg hover:bg-primary-700 transition-colors"
        >
          <Plus size={20} />
          Record Sale
        </button>
      </div>

      {/* Messages */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2 text-red-700 dark:text-red-300 transition-colors duration-200">
          <AlertCircle size={20} />
          {error}
        </div>
      )}

      {success && (
        <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-green-700 dark:text-green-300 transition-colors duration-200">
          {success}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white dark:bg-dark-card rounded-lg shadow-sm border border-gray-200 dark:border-dark-border p-4 mb-6 transition-colors duration-200">
        <div className="flex flex-wrap gap-4">
          {/* Month Filter */}
          <StyledDropdown
            options={months.map(m => ({ value: m.value, label: m.label }))}
              value={filterMonth}
            onChange={(val) => setFilterMonth(parseInt(val))}
            placeholder="Select Month"
            icon={Calendar}
            className="min-w-[150px]"
          />

          {/* Year Filter */}
          <StyledDropdown
            options={years.map(y => ({ value: y, label: y.toString() }))}
              value={filterYear}
            onChange={(val) => setFilterYear(parseInt(val))}
            placeholder="Select Year"
            className="min-w-[120px]"
          />

          {/* Group Filter */}
          <StyledDropdown
            options={[
              { value: '', label: 'All Groups' },
              ...groups.filter(g => g.status === 'Active').map(g => ({ value: g.id.toString(), label: g.name }))
            ]}
              value={filterGroup}
            onChange={(val) => {
              setFilterGroup(val);
                setFilterUser(''); // Reset user filter when group changes
              }}
            placeholder="All Groups"
            icon={Building2}
            showClear={true}
            className="min-w-[180px]"
          />

          {/* User Filter */}
          <div className="flex items-center gap-2">
            <User size={20} className="text-gray-400 dark:text-dark-muted" />
            <SearchableUserDropdown
              users={filteredUsers}
              value={filterUser}
              onChange={setFilterUser}
              placeholder={`All Users${filterGroup ? ' in Group' : ''}`}
              maxResults={50}
            />
          </div>

          {/* Product Filter */}
          <StyledDropdown
            options={[
              { value: '', label: 'All Products' },
              ...products.map(p => ({ value: p.id.toString(), label: p.name }))
            ]}
              value={filterProduct}
            onChange={setFilterProduct}
            placeholder="All Products"
            icon={Package}
            showClear={true}
            className="min-w-[300px]"
          />
        </div>
      </div>

      {/* Sales Table */}
      <div className="bg-white dark:bg-dark-card rounded-lg shadow-sm border border-gray-200 dark:border-dark-border overflow-hidden transition-colors duration-200">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-slate-800/50 border-b border-gray-200 dark:border-dark-border">
              <tr>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 dark:text-dark-muted uppercase tracking-wider">
                  ID
                </th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 dark:text-dark-muted uppercase tracking-wider">
                  Date
                </th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 dark:text-dark-muted uppercase tracking-wider">
                  Sales Person
                </th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 dark:text-dark-muted uppercase tracking-wider">
                  Product
                </th>
                <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 dark:text-dark-muted uppercase tracking-wider">
                  Amount
                </th>
                <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 dark:text-dark-muted uppercase tracking-wider">
                  Recorded By
                </th>
                <th className="text-center px-6 py-3 text-xs font-medium text-gray-500 dark:text-dark-muted uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-dark-border">
              {sales.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-12 text-center text-gray-500 dark:text-dark-muted">
                    <DollarSign size={48} className="mx-auto mb-4 text-gray-300 dark:text-slate-600" />
                    <p className="text-lg font-medium dark:text-dark-text">No sales records found</p>
                    <p className="text-sm dark:text-dark-muted">Start by recording a sale</p>
                  </td>
                </tr>
              ) : (
                paginatedSales.map((sale) => (
                  <tr key={sale.id} className="hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-dark-text">
                      #{sale.id}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-dark-text">
                      {formatDate(sale.date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-dark-text">{sale.user.name}</div>
                        <div className="text-sm text-gray-500 dark:text-dark-muted">{sale.user.role.name}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-dark-text">
                      {sale.product.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-medium text-gray-900 dark:text-dark-text">
                      {formatCurrency(sale.amount)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-dark-muted">
                      {sale.recorder.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => openViewModal(sale)}
                          className="text-primary-600 hover:text-primary-800 p-1"
                          title="View Details"
                        >
                          <Search size={18} />
                        </button>
                        <button
                          onClick={() => openEditModal(sale)}
                          className="text-gray-600 dark:text-dark-muted hover:text-gray-800 dark:hover:text-dark-text p-1 transition-colors"
                          title="Edit"
                        >
                          <Edit size={18} />
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(sale)}
                          className="text-red-600 hover:text-red-800 p-1"
                          title="Delete"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        <Pagination
          currentPage={currentPage}
          totalItems={totalItems}
          itemsPerPage={10}
          onPageChange={handlePageChange}
        />
      </div>

      {/* Summary */}
      {sales.length > 0 && (
        <div className="mt-4 bg-white dark:bg-dark-card rounded-lg shadow-sm border border-gray-200 dark:border-dark-border p-4 transition-colors duration-200">
          <div className="flex justify-between items-center">
            <span className="text-gray-600 dark:text-dark-muted">Total Sales ({sales.length} records)</span>
            <span className="text-xl font-bold text-gray-900 dark:text-dark-text">
              {formatCurrency(sales.reduce((sum, s) => sum + parseFloat(s.amount), 0))}
            </span>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-dark-card rounded-lg shadow-xl w-full max-w-lg mx-4 transition-colors duration-200">
            <div className="flex items-center justify-between p-4 border-b dark:border-dark-border">
              <h2 className="text-lg font-semibold dark:text-dark-text">Record New Sale</h2>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-400 dark:text-dark-muted hover:text-gray-600 dark:hover:text-dark-text transition-colors"
              >
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleCreateSale} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">
                  Sales Person *
                </label>
                <SearchableUserDropdown
                  users={users}
                  value={formData.userId}
                  onChange={(val) => setFormData({ ...formData, userId: val })}
                  placeholder="Select User"
                  maxResults={50}
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">
                  Product *
                </label>
                <StyledDropdown
                  options={[
                    { value: '', label: 'Select Product' },
                    ...products.map(p => ({ value: p.id.toString(), label: p.name }))
                  ]}
                  value={formData.productId}
                  onChange={(val) => setFormData({ ...formData, productId: val })}
                  placeholder="Select Product"
                  icon={Package}
                  className="w-full"
                />
              </div>

              {/* Show product commission info */}
              {getSelectedProduct() && (
                <div className="bg-gray-50 dark:bg-slate-800/50 rounded-lg p-3 text-sm transition-colors duration-200">
                  <div className="font-medium text-gray-700 dark:text-dark-text mb-2">Commission Rates:</div>
                  <div className="grid grid-cols-3 gap-2 text-gray-600 dark:text-dark-muted">
                    <div>Agent: {parseFloat(getSelectedProduct().agentComm).toFixed(2)}%</div>
                    <div>TL: {parseFloat(getSelectedProduct().teamLeaderComm).toFixed(2)}%</div>
                    <div>GL: {parseFloat(getSelectedProduct().groupLeaderComm).toFixed(2)}%</div>
                  </div>
                  {(getSelectedProduct().tlOverrideThreshold || getSelectedProduct().glOverrideThreshold) && (
                    <div className="mt-2 pt-2 border-t border-gray-200 dark:border-dark-border">
                      <div className="font-medium text-gray-700 dark:text-dark-text mb-1">Thresholds:</div>
                      <div className="text-gray-600 dark:text-dark-muted">
                        {getSelectedProduct().tlOverrideThreshold && (
                          <div>TL Override: {formatCurrency(getSelectedProduct().tlOverrideThreshold)}</div>
                        )}
                        {getSelectedProduct().glOverrideThreshold && (
                          <div>GL Override: {formatCurrency(getSelectedProduct().glOverrideThreshold)}</div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">
                  Amount (RM) *
                </label>
                <input
                  type="text"
                  value={formatNumber(formData.amount)}
                  onChange={(e) => {
                    const rawValue = parseFormattedNumber(e.target.value);
                    if (rawValue === '' || /^\d*\.?\d*$/.test(rawValue)) {
                      setFormData({ ...formData, amount: rawValue });
                    }
                  }}
                  className="w-full border border-gray-300 dark:border-dark-border dark:bg-dark-bg dark:text-dark-text rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                  placeholder="e.g., 500,000"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">
                  Date *
                </label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="w-full border border-gray-300 dark:border-dark-border dark:bg-dark-bg dark:text-dark-text rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">
                  Client Name *
                </label>
                <input
                  type="text"
                  value={formData.clientName}
                  onChange={(e) => setFormData({ ...formData, clientName: e.target.value })}
                  className="w-full border border-gray-300 dark:border-dark-border dark:bg-dark-bg dark:text-dark-text rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                  placeholder="Enter client name"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full border border-gray-300 dark:border-dark-border dark:bg-dark-bg dark:text-dark-text rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                  rows="3"
                  placeholder="Optional notes..."
                />
              </div>

              {/* Commission Preview */}
              {formData.amount && formData.productId && formData.userId && (
                <div className="bg-primary-50 dark:bg-primary-900/20 rounded-lg p-3 transition-colors duration-200">
                  <div className="font-medium text-primary-800 dark:text-primary-300 mb-2">Commission Preview:</div>
                  <div className="space-y-1 text-sm text-primary-700 dark:text-primary-300">
                    {(() => {
                      const product = getSelectedProduct();
                      const amount = parseFloat(formData.amount);
                      if (!product || !amount) return null;
                      // Rates are stored as percentages (e.g., 2.5 means 2.5%), divide by 100 for calculation
                      return (
                        <>
                          <div className="flex justify-between">
                            <span>Personal (Agent):</span>
                            <span>{formatCurrency(amount * parseFloat(product.agentComm) / 100)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>TL Override:</span>
                            <span>{formatCurrency(amount * parseFloat(product.teamLeaderComm) / 100)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>GL Override:</span>
                            <span>{formatCurrency(amount * parseFloat(product.groupLeaderComm) / 100)}</span>
                          </div>
                        </>
                      );
                    })()}
                  </div>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-dark-border dark:bg-dark-bg dark:text-dark-text rounded-lg text-gray-700 dark:text-dark-text hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  Record Sale
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && editingSale && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-dark-card rounded-lg shadow-xl w-full max-w-lg mx-4 transition-colors duration-200">
            <div className="flex items-center justify-between p-4 border-b dark:border-dark-border">
              <h2 className="text-lg font-semibold dark:text-dark-text">Edit Sale #{editingSale.id}</h2>
              <button
                onClick={() => {
                  setShowEditModal(false);
                  setEditingSale(null);
                }}
                className="text-gray-400 dark:text-dark-muted hover:text-gray-600 dark:hover:text-dark-text transition-colors"
              >
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleUpdateSale} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">
                  Sales Person *
                </label>
                <SearchableUserDropdown
                  users={users}
                  value={formData.userId}
                  onChange={(val) => setFormData({ ...formData, userId: val })}
                  placeholder="Select User"
                  maxResults={50}
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">
                  Product *
                </label>
                <StyledDropdown
                  options={[
                    { value: '', label: 'Select Product' },
                    ...products.map(p => ({ value: p.id.toString(), label: p.name }))
                  ]}
                  value={formData.productId}
                  onChange={(val) => setFormData({ ...formData, productId: val })}
                  placeholder="Select Product"
                  icon={Package}
                  className="w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">
                  Amount (RM) *
                </label>
                <input
                  type="text"
                  value={formatNumber(formData.amount)}
                  onChange={(e) => {
                    const rawValue = parseFormattedNumber(e.target.value);
                    if (rawValue === '' || /^\d*\.?\d*$/.test(rawValue)) {
                      setFormData({ ...formData, amount: rawValue });
                    }
                  }}
                  className="w-full border border-gray-300 dark:border-dark-border dark:bg-dark-bg dark:text-dark-text rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                  placeholder="e.g., 500,000"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">
                  Date *
                </label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="w-full border border-gray-300 dark:border-dark-border dark:bg-dark-bg dark:text-dark-text rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">
                  Client Name *
                </label>
                <input
                  type="text"
                  value={formData.clientName}
                  onChange={(e) => setFormData({ ...formData, clientName: e.target.value })}
                  className="w-full border border-gray-300 dark:border-dark-border dark:bg-dark-bg dark:text-dark-text rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                  placeholder="Enter client name"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-dark-text mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full border border-gray-300 dark:border-dark-border dark:bg-dark-bg dark:text-dark-text rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors duration-200"
                  rows="3"
                  placeholder="Optional notes..."
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingSale(null);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-dark-border dark:bg-dark-bg dark:text-dark-text rounded-lg text-gray-700 dark:text-dark-text hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  Update Sale
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && viewingSale && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-dark-card rounded-lg shadow-xl w-full max-w-lg mx-4 transition-colors duration-200">
            <div className="flex items-center justify-between p-4 border-b dark:border-dark-border">
              <h2 className="text-lg font-semibold dark:text-dark-text">Sale Details #{viewingSale.id}</h2>
              <button
                onClick={() => {
                  setShowViewModal(false);
                  setViewingSale(null);
                }}
                className="text-gray-400 dark:text-dark-muted hover:text-gray-600 dark:hover:text-dark-text transition-colors"
              >
                <X size={24} />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-gray-500 dark:text-dark-muted">Date</div>
                  <div className="font-medium dark:text-dark-text">{formatDate(viewingSale.date)}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 dark:text-dark-muted">Amount</div>
                  <div className="font-medium text-lg dark:text-dark-text">{formatCurrency(viewingSale.amount)}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 dark:text-dark-muted">Sales Person</div>
                  <div className="font-medium dark:text-dark-text">{viewingSale.user.name}</div>
                  <div className="text-sm text-gray-500 dark:text-dark-muted">{viewingSale.user.role.name}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-500 dark:text-dark-muted">Product</div>
                  <div className="font-medium dark:text-dark-text">{viewingSale.product.name}</div>
                </div>
              </div>

              {viewingSale.description && (
                <div>
                  <div className="text-sm text-gray-500 dark:text-dark-muted">Description</div>
                  <div className="font-medium dark:text-dark-text">{viewingSale.description}</div>
                </div>
              )}

              <div className="border-t dark:border-dark-border pt-4">
                <div className="text-sm font-medium text-gray-700 dark:text-dark-text mb-3">Commission Records</div>
                <div className="space-y-2">
                  {viewingSale.commissionRecords.map((comm) => (
                    <div
                      key={comm.id}
                      className="flex justify-between items-center bg-gray-50 dark:bg-slate-800/50 rounded-lg p-3 transition-colors duration-200"
                    >
                      <div>
                        <div className="font-medium text-gray-900 dark:text-dark-text">{comm.user.name}</div>
                        <div className="text-sm text-gray-500 dark:text-dark-muted">
                          {comm.commissionType} ({(parseFloat(comm.commissionRate) * 100).toFixed(2)}%)
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="font-medium text-primary-600 dark:text-primary-400">
                          {formatCurrency(comm.commissionAmount)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="text-sm text-gray-500 dark:text-dark-muted pt-2 border-t dark:border-dark-border">
                Recorded by {viewingSale.recorder.name} on{' '}
                {new Date(viewingSale.createdAt).toLocaleString()}
              </div>

              <button
                onClick={() => {
                  setShowViewModal(false);
                  setViewingSale(null);
                }}
                className="w-full px-4 py-2 bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-dark-text rounded-lg hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors duration-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-dark-card rounded-lg shadow-xl w-full max-w-md mx-4 p-6 transition-colors duration-200">
            <div className="flex items-center gap-3 text-red-600 dark:text-red-400 mb-4">
              <AlertCircle size={24} />
              <h2 className="text-lg font-semibold dark:text-dark-text">Delete Sale</h2>
            </div>
            <p className="text-gray-600 dark:text-dark-muted mb-2">
              Are you sure you want to delete this sale record?
            </p>
            <p className="text-sm text-gray-500 dark:text-dark-muted mb-4">
              Sale #{deleteConfirm.id} - {formatCurrency(deleteConfirm.amount)} by{' '}
              {deleteConfirm.user.name}
            </p>
            <p className="text-sm text-red-600 dark:text-red-400 mb-4">
              This will also delete all associated commission records.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-dark-border dark:bg-dark-bg dark:text-dark-text rounded-lg text-gray-700 dark:text-dark-text hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteSale(deleteConfirm.id)}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesManagement;

